import { useEffect, useState } from "react";

/* ============================================================
   Reações

   Uma por pessoa: o documento é `reactions/{uid}`, então o limite é o id
   e não uma conferência. Tocar de novo no mesmo emoji desfaz — é como a
   nota já se comporta, e repetir o gesto que a pessoa já conhece vale
   mais que inventar um segundo.

   Os cinco fixos são os que aparecem sem pensar. O resto está atrás do
   (+) porque uma barra com quarenta emojis não é escolha, é busca.
   ============================================================ */

export const REACOES_FIXAS = ["🔥", "😍", "🤤", "😂", "👏"] as const;

/** A grade do (+). Comida primeiro, porque é disso que se trata. */
const GRADE: readonly (readonly [string, readonly string[]])[] = [
  ["Prato", ["🍕", "🍔", "🌮", "🍣", "🍜", "🥗", "🍝", "🥩", "🍟", "🥐", "🍞", "🧀"]],
  ["Doce", ["🍰", "🍫", "🍩", "🍦", "🧁", "🍪", "🍮", "🍯"]],
  ["Bebida", ["☕", "🍺", "🍷", "🥤", "🧃", "🍸"]],
  ["Reação", ["😋", "🤯", "😳", "🥲", "😴", "🙃", "💀", "🫡", "🙏", "💪", "👀", "🫶"]],
  ["Sincero", ["🤢", "🥴", "😬", "🧊", "🧂", "💸", "🐀", "🚑"]],
];

/**
 * A barra que abre no toque longo.
 *
 * Fecha em qualquer toque fora e no `Escape`. Sem isso ela ficaria presa
 * na tela quando a pessoa desiste — e desistir é a metade dos casos.
 */
export function BarraDeReacao(
  { minha, aoEscolher, aoFechar }:
  { minha: string | null; aoEscolher: (emoji: string) => void; aoFechar: () => void },
) {
  const [grade, setGrade] = useState(false);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") aoFechar(); };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [aoFechar]);

  return (
    <div className="reacao-fundo" onClick={aoFechar} role="presentation">
      <div className={`reacao-caixa ${grade ? "grade" : ""}`}
        onClick={(e) => e.stopPropagation()}>
        {!grade ? (
          <div className="reacao-barra">
            {REACOES_FIXAS.map((e) => (
              <button key={e} className={`reacao-btn ${minha === e ? "minha" : ""}`}
                onClick={() => aoEscolher(e)} aria-label={`Reagir com ${e}`}>
                {e}
              </button>
            ))}
            <button className="reacao-btn mais" onClick={() => setGrade(true)}
              aria-label="Mais emojis">
              +
            </button>
          </div>
        ) : (
          <div className="reacao-grade">
            {GRADE.map(([titulo, emojis]) => (
              <div key={titulo} className="reacao-grupo">
                <div className="reacao-grupo-nome">{titulo}</div>
                <div className="reacao-grupo-itens">
                  {emojis.map((e) => (
                    <button key={e} className={`reacao-btn ${minha === e ? "minha" : ""}`}
                      onClick={() => aoEscolher(e)} aria-label={`Reagir com ${e}`}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * O resumo no card: emoji e quantos.
 *
 * Vem do mapa que o servidor agrega no prato, não da subcoleção — o feed
 * desenha dezenas de cards, e uma consulta por card seria uma consulta
 * por card.
 */
export function ResumoDeReacoes(
  { reactions, minha }: { reactions?: Record<string, number>; minha?: string | null },
) {
  const lista = Object.entries(reactions ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (!lista.length) return null;

  return (
    <div className="reacao-resumo">
      {lista.map(([emoji, n]) => (
        <span key={emoji} className={`reacao-selo ${minha === emoji ? "minha" : ""}`}>
          {emoji}{n > 1 && <i>{n}</i>}
        </span>
      ))}
    </div>
  );
}
